// Media Kit View - Print/PDF-friendly layout optimized for sharing with brands
// Clean, professional design that works well when printed or exported

import React, { useState, useEffect } from 'react';
import { getInfluencerByUsername } from '../../../lib/models/influencer';

const MediaKit = ({ onNavigate }) => {
  const [username, setUsername] = useState('');
  const [influencerProfile, setInfluencerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(Boolean);
    const pathUsername = path.startsWith('/u/') && pathParts.length >= 2
      ? pathParts[1]
      : pathParts[0];
    setUsername(pathUsername || '');

    const loadProfile = async () => {
      if (!pathUsername) {
        setError('Username not provided');
        setLoading(false);
        return;
      }

      try {
        const influencer = await getInfluencerByUsername(pathUsername);
        if (influencer) {
          setInfluencerProfile(influencer);
        } else {
          setError('Profile not found');
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleDownloadPDF = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center print:hidden">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading media kit...</p>
        </div>
      </div>
    );
  }

  if (error || !influencerProfile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center print:hidden">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Media Kit Not Found</h1>
          <p className="text-gray-600 mb-6">
            {error || 'The media kit you\'re looking for doesn\'t exist.'}
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  const topPlatforms = influencerProfile.platforms
    ?.sort((a, b) => (b.followers || 0) - (a.followers || 0))
    .slice(0, 3) || [];

  const topCollaborations = influencerProfile.collaborations
    ?.slice(0, 5) || [];

  const topPortfolio = influencerProfile.portfolio
    ?.slice(0, 6) || [];

  const niches = Array.isArray(influencerProfile.niche) 
    ? influencerProfile.niche 
    : influencerProfile.niche ? [influencerProfile.niche] : [];

  return (
    <div className="min-h-screen bg-white">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-gray-50 border-b border-gray-200 p-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Media Kit</h1>
            <p className="text-sm text-gray-600">{influencerProfile.displayName}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleDownloadPDF}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium shadow-sm"
            >
              Print / Save as PDF
            </button>
            <a
              href={`/${influencerProfile.username}`}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              View Profile
            </a>
          </div>
        </div>
      </div>

      {/* Media Kit Content - Optimized for Print */}
      <div className="max-w-4xl mx-auto px-8 py-12 print:px-0 print:py-8">
        {/* Header Section */}
        <div className="mb-10 print:mb-8 border-b border-gray-200 pb-8 print:pb-6">
          <div className="flex items-center gap-6 mb-6 print:mb-4">
            {influencerProfile.profileImageUrl && (
              <img
                src={influencerProfile.profileImageUrl}
                alt={influencerProfile.displayName}
                className="h-28 w-28 rounded-full object-cover border-4 border-gray-200 print:h-24 print:w-24"
              />
            )}
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-gray-900 mb-2 print:text-3xl">
                {influencerProfile.displayName}
              </h1>
              <p className="text-xl text-gray-600 mb-3 print:text-lg">
                @{influencerProfile.username}
              </p>
              {niches.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {niches.map((n, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm print:text-xs print:px-2 print:py-0.5"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {influencerProfile.bio && (
            <p className="text-gray-700 leading-relaxed text-lg print:text-base">
              {influencerProfile.bio}
            </p>
          )}
        </div>

        {/* Stats Section */}
        <div className="mb-10 print:mb-8 grid grid-cols-2 md:grid-cols-4 gap-6 print:grid-cols-4 print:gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900 mb-1 print:text-2xl">
              {influencerProfile.stats?.totalFollowers?.toLocaleString() || 'N/A'}
            </p>
            <p className="text-sm text-gray-600 print:text-xs">Total Followers</p>
          </div>
          {influencerProfile.stats?.engagementRate != null && (
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900 mb-1 print:text-2xl">
                {typeof influencerProfile.stats.engagementRate === 'number' 
                  ? influencerProfile.stats.engagementRate.toFixed(1) 
                  : parseFloat(influencerProfile.stats.engagementRate)?.toFixed(1) || '0.0'}%
              </p>
              <p className="text-sm text-gray-600 print:text-xs">Engagement Rate</p>
            </div>
          )}
          {topPlatforms.length > 0 && (
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 mb-1 print:text-xl">
                {topPlatforms[0].platformName}
              </p>
              <p className="text-sm text-gray-600 print:text-xs">
                {topPlatforms[0].followers?.toLocaleString()} followers
              </p>
            </div>
          )}
          {influencerProfile.portfolio && influencerProfile.portfolio.length > 0 && (
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900 mb-1 print:text-2xl">
                {influencerProfile.portfolio.length}
              </p>
              <p className="text-sm text-gray-600 print:text-xs">Portfolio Items</p>
            </div>
          )}
        </div>

        {/* Platforms Section */}
        {topPlatforms.length > 0 && (
          <div className="mb-10 print:mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 print:text-xl print:mb-3 border-b border-gray-200 pb-2">
              Platform Breakdown
            </h2>
            <div className="space-y-3 print:space-y-2">
              {topPlatforms.map((platform, index) => (
                <div key={index} className="flex items-center justify-between py-2 print:py-1">
                  <div>
                    <p className="font-semibold text-gray-900 print:text-sm">{platform.platformName}</p>
                    <p className="text-sm text-gray-600 print:text-xs">@{platform.handle}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 print:text-sm">
                      {platform.followers?.toLocaleString() || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-600">followers</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Collaborations Section */}
        {topCollaborations.length > 0 && (
          <div className="mb-10 print:mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 print:text-xl print:mb-3 border-b border-gray-200 pb-2">
              Past Collaborations
            </h2>
            <div className="space-y-4 print:space-y-3">
              {topCollaborations.map((collab) => (
                <div key={collab.id} className="print:break-inside-avoid">
                  <div className="flex items-start gap-3">
                    {collab.brandLogoUrl && (
                      <img
                        src={collab.brandLogoUrl}
                        alt={collab.brandName}
                        className="h-12 w-12 rounded object-cover print:h-10 print:w-10"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 print:text-sm mb-1">
                        {collab.brandName}
                      </h3>
                      <p className="text-sm text-gray-600 print:text-xs">
                        {collab.description}
                      </p>
                      {collab.metrics && (
                        <div className="mt-2 flex gap-4 text-xs text-gray-500 print:text-xs">
                          {collab.metrics.views && (
                            <span>{collab.metrics.views.toLocaleString()} views</span>
                          )}
                          {collab.metrics.engagement && (
                            <span>{collab.metrics.engagement.toLocaleString()} engagement</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio Section */}
        {topPortfolio.length > 0 && (
          <div className="mb-10 print:mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 print:text-xl print:mb-3 border-b border-gray-200 pb-2">
              Selected Portfolio
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 print:grid-cols-3 print:gap-3">
              {topPortfolio.map((item) => (
                <div key={item.id} className="print:break-inside-avoid">
                  {item.thumbnailUrl && (
                    <img
                      src={item.thumbnailUrl}
                      alt={item.title}
                      className="w-full aspect-square object-cover rounded-lg mb-2 print:mb-1 print:rounded"
                    />
                  )}
                  <h3 className="font-semibold text-gray-900 text-sm print:text-xs mb-1">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-xs text-gray-600 print:text-xs line-clamp-2">
                      {item.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Section */}
        <div className="border-t border-gray-200 pt-8 print:pt-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 print:text-xl print:mb-3">
            Contact
          </h2>
          <p className="text-gray-700 print:text-sm">
            For collaboration inquiries, please visit{' '}
            <span className="font-medium">{typeof window !== "undefined" ? window.location.host : "influencehub.net"}/u/{influencerProfile.username}</span>
          </p>
          <p className="text-sm text-gray-500 mt-2 print:text-xs">
            This profile is automatically updated. Share this link instead of a static PDF.
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 1.5cm;
            size: A4;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white;
          }
          .print\\:hidden {
            display: none !important;
          }
          * {
            color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          img {
            max-width: 100%;
            height: auto;
          }
          a {
            color: #2563eb;
            text-decoration: underline;
          }
        }
      `}</style>
    </div>
  );
};

export default MediaKit;
